import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

// Pick the part of a song a show uses: waveform + bar grid from the engine's analysis, drag to select
// (snapping to bars/beats), audition with loop + metronome, save as a clip in the show document.
// Talks to the engine through the preview front door (/engine/...).
const API = '/engine';
const C = {bg: '#0b0c0f', panel: '#15171c', line: '#262a33', text: '#e8eaed', dim: '#8a9099', accent: '#19f0a0', sel: '#19f0a0'};
const panel = {background: C.panel, border: `1px solid ${C.line}`, borderRadius: 10, overflow: 'hidden', minHeight: 0};
const heading = {fontSize: 11, letterSpacing: 1.2, textTransform: 'uppercase', color: C.dim, padding: '8px 12px'};
const btn = {background: '#1f232b', color: C.text, border: `1px solid ${C.line}`, borderRadius: 6, padding: '5px 12px', cursor: 'pointer', fontSize: 12};
const input = {background: C.bg, color: C.text, border: `1px solid ${C.line}`, borderRadius: 6, padding: '5px 8px', fontSize: 12};

const fmt = (t) => `${Math.floor(t / 60)}:${(t % 60).toFixed(2).padStart(5, '0')}`;

// The bar grid: beat k sits at firstBeat + k·(60/bpm); bars start where (k − barPhase) is a multiple of beatsPerBar.
const gridOf = (analysis) => analysis && analysis.grid;
const beatLen = (g) => 60 / g.bpm;
const beatIndexAt = (g, t) => (t - g.firstBeat) / beatLen(g);
const barLabel = (g, t) => {
    const k = Math.round(beatIndexAt(g, t)) - g.barPhase;
    return `${Math.floor(k / g.beatsPerBar) + 1}.${((k % g.beatsPerBar) + g.beatsPerBar) % g.beatsPerBar + 1}`;
};
const snapTime = (g, t, mode) => {
    if (!g || mode === 'off') return t;
    const step = mode === 'bar' ? g.beatsPerBar : 1;
    const k = beatIndexAt(g, t) - (mode === 'bar' ? g.barPhase : 0);
    return g.firstBeat + (Math.round(k / step) * step + (mode === 'bar' ? g.barPhase : 0)) * beatLen(g);
};

const Library = ({items, selectedId, onSelect, onImported}) => {
    const [path, setPath] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const done = (p) => p.then(r => r.json()).then(m => { if (m.error) throw new Error(m.error); onImported(m); })
        .catch(e => setError(e.message)).finally(() => setBusy(false));
    const importPath = () => { if (!path.trim()) return; setBusy(true); setError(null);
        done(fetch(`${API}/media/import`, {method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({path: path.trim().replace(/\\ /g, ' ')})})); };
    const onDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (!file) return;
        setBusy(true); setError(null);
        done(fetch(`${API}/media/upload?name=${encodeURIComponent(file.name)}`, {method: 'POST', body: file}));
    };
    return <div style={{...panel, display: 'flex', flexDirection: 'column'}} onDragOver={e => e.preventDefault()} onDrop={onDrop}>
        <div style={heading}>Library</div>
        <div style={{padding: '0 10px 10px', display: 'flex', flexDirection: 'column', gap: 6}}>
            <div style={{border: `1px dashed ${C.line}`, borderRadius: 8, padding: 12, textAlign: 'center', color: C.dim, fontSize: 12}}>
                {busy ? 'Importing…' : 'Drop a song here'}
            </div>
            <div style={{display: 'flex', gap: 6}}>
                <input value={path} onChange={e => setPath(e.target.value)} onKeyDown={e => e.key === 'Enter' && importPath()}
                    placeholder="…or paste a file path" style={{...input, flex: 1, minWidth: 0}}/>
                <button onClick={importPath} style={btn}>Import</button>
            </div>
            {error && <div style={{color: '#ff6b6b', fontSize: 11}}>{error}</div>}
        </div>
        <div style={{overflowY: 'auto', flex: 1}}>
            {items.map(m => <div key={m.id} onClick={() => onSelect(m.id)} style={{
                padding: '8px 12px', cursor: 'pointer', borderLeft: `3px solid ${m.id === selectedId ? C.accent : 'transparent'}`,
                background: m.id === selectedId ? '#1f232b' : 'transparent'}}>
                <div style={{fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{m.name}</div>
                <div style={{fontSize: 11, color: m.status === 'failed' ? '#ff6b6b' : C.dim}}>{m.status === 'ready' ? 'analyzed' : m.status}</div>
            </div>)}
        </div>
    </div>;
};

// Waveform + grid + selection on a canvas. View = {start (s at left edge), pps (pixels per second)}.
const Waveform = ({analysis, view, setView, selection, setSelection, playhead, snap, onSeek, fadeIn, fadeOut}) => {
    const wrap = useRef(null);
    const canvas = useRef(null);
    const drag = useRef(null);
    const [width, setWidth] = useState(800);
    const height = 340;
    const g = gridOf(analysis);

    useEffect(() => {
        const ro = new ResizeObserver(() => wrap.current && setWidth(wrap.current.clientWidth));
        ro.observe(wrap.current);
        return () => ro.disconnect();
    }, []);

    useEffect(() => {
        const cv = canvas.current;
        if (!cv || !analysis) return;
        const dpr = window.devicePixelRatio || 1;
        cv.width = width * dpr; cv.height = height * dpr;
        const ctx = cv.getContext('2d');
        ctx.scale(dpr, dpr);
        ctx.fillStyle = '#0d0f13'; ctx.fillRect(0, 0, width, height);
        const x = (t) => (t - view.start) * view.pps;
        const tEnd = view.start + width / view.pps;

        if (selection) {
            ctx.fillStyle = 'rgba(25,240,160,0.10)';
            ctx.fillRect(x(selection.start), 0, x(selection.end) - x(selection.start), height);
        }
        if (g) {
            const bl = beatLen(g);
            const showBeats = bl * view.pps > 9;
            const barEvery = g.beatsPerBar * bl * view.pps > 34 ? 1 : g.beatsPerBar * bl * view.pps > 9 ? 4 : 16;
            for (let k = Math.max(0, Math.floor(beatIndexAt(g, view.start))); ; k++) {
                const t = g.firstBeat + k * bl;
                if (t > tEnd) break;
                const rel = k - g.barPhase;
                const isBar = ((rel % g.beatsPerBar) + g.beatsPerBar) % g.beatsPerBar === 0;
                const bar = Math.floor(rel / g.beatsPerBar) + 1;
                if (isBar && (bar - 1) % barEvery === 0) {
                    ctx.fillStyle = 'rgba(255,255,255,0.22)'; ctx.fillRect(x(t), 0, 1, height);
                    ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.font = '10px system-ui'; ctx.fillText(String(bar), x(t) + 3, 11);
                } else if (showBeats && !isBar) {
                    ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(x(t), 16, 1, height - 16);
                }
            }
        }
        const {peaks, peaksPerSec} = analysis;
        const mid = height / 2 + 6, amp = height / 2 - 14;
        for (let px = 0; px < width; px++) {
            const t0 = view.start + px / view.pps, t1 = t0 + 1 / view.pps;
            let i0 = Math.max(0, Math.floor(t0 * peaksPerSec)), i1 = Math.min(peaks.length, Math.max(i0 + 1, Math.ceil(t1 * peaksPerSec)));
            let p = 0;
            for (let i = i0; i < i1; i++) if (peaks[i] > p) p = peaks[i];
            const inSel = selection && t0 >= selection.start && t0 <= selection.end;
            let gain = 1;
            if (inSel && fadeIn > 0 && t0 < selection.start + fadeIn) gain = (t0 - selection.start) / fadeIn;
            if (inSel && fadeOut > 0 && t0 > selection.end - fadeOut) gain = Math.min(gain, (selection.end - t0) / fadeOut);
            ctx.fillStyle = inSel ? C.sel : '#4b5360';
            const h = Math.max(1, p * amp * (inSel ? Math.max(0.05, gain) : 1));
            ctx.fillRect(px, mid - h, 1, h * 2);
        }
        if (selection) {
            ctx.fillStyle = C.sel;
            ctx.fillRect(x(selection.start) - 1, 0, 2, height); ctx.fillRect(x(selection.end) - 1, 0, 2, height);
        }
        if (playhead != null && playhead >= view.start && playhead <= tEnd) {
            ctx.fillStyle = '#fff'; ctx.fillRect(x(playhead), 0, 2, height);
        }
    }, [analysis, view, selection, playhead, width, g, fadeIn, fadeOut]);

    // Pinch / ⌘+scroll zooms around the cursor; plain horizontal scroll pans.
    useEffect(() => {
        const el = wrap.current;
        const onWheel = (e) => {
            e.preventDefault();
            const r = el.getBoundingClientRect(), mx = e.clientX - r.left;
            setView(v => {
                if (e.ctrlKey || e.metaKey) {
                    const tAt = v.start + mx / v.pps;
                    const pps = Math.min(2000, Math.max(width / (analysis ? analysis.duration : 300), v.pps * Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0025))));
                    return {pps, start: Math.max(0, tAt - mx / pps)};
                }
                const d = (Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY) / v.pps;
                return {...v, start: Math.max(0, v.start + d)};
            });
        };
        el.addEventListener('wheel', onWheel, {passive: false});
        return () => el.removeEventListener('wheel', onWheel);
    }, [setView, width, analysis]);

    const timeAt = (e) => { const r = wrap.current.getBoundingClientRect(); return Math.max(0, view.start + (e.clientX - r.left) / view.pps); };
    const onDown = (e) => {
        const t = timeAt(e), px = (tt) => (tt - view.start) * view.pps, mx = e.clientX - wrap.current.getBoundingClientRect().left;
        if (selection && Math.abs(px(selection.start) - mx) < 7) drag.current = {edge: 'start'};
        else if (selection && Math.abs(px(selection.end) - mx) < 7) drag.current = {edge: 'end'};
        else drag.current = {edge: 'new', anchor: snapTime(g, t, snap), downX: e.clientX, t};
    };
    const onMove = (e) => {
        const d = drag.current;
        if (!d) return;
        const t = snapTime(g, timeAt(e), snap);
        if (d.edge === 'new') {
            if (Math.abs(e.clientX - d.downX) < 4) return;
            setSelection({start: Math.min(d.anchor, t), end: Math.max(d.anchor, t)});
        } else setSelection(s => {
            const next = {...s, [d.edge]: t};
            return next.end > next.start ? next : s;
        });
    };
    const onUp = (e) => {
        const d = drag.current;
        drag.current = null;
        if (d && d.edge === 'new' && Math.abs(e.clientX - d.downX) < 4) {
            if (selection && (d.t < selection.start || d.t > selection.end)) setSelection(null);   // click outside = deselect
            onSeek(d.t);
        }
    };
    const cursor = (e) => {
        if (!selection || drag.current) return;
        const mx = e.clientX - wrap.current.getBoundingClientRect().left, px = (tt) => (tt - view.start) * view.pps;
        e.currentTarget.style.cursor = Math.abs(px(selection.start) - mx) < 7 || Math.abs(px(selection.end) - mx) < 7 ? 'ew-resize' : 'crosshair';
    };
    return <div ref={wrap} onMouseDown={onDown} onMouseMove={(e) => { onMove(e); cursor(e); }} onMouseUp={onUp} onMouseLeave={() => { drag.current = null; }}
        style={{position: 'relative', height, cursor: 'crosshair', userSelect: 'none'}}>
        <canvas ref={canvas} style={{width: '100%', height, display: 'block', borderRadius: 8}}/>
    </div>;
};

// Whole song in one strip; the box shows what the main waveform is looking at. Drag to scroll.
const Overview = ({analysis, view, setView, mainWidth}) => {
    const canvas = useRef(null);
    const wrap = useRef(null);
    const height = 36;
    useEffect(() => {
        const cv = canvas.current, w = wrap.current && wrap.current.clientWidth;
        if (!cv || !analysis || !w) return;
        const dpr = window.devicePixelRatio || 1;
        cv.width = w * dpr; cv.height = height * dpr;
        const ctx = cv.getContext('2d'); ctx.scale(dpr, dpr);
        ctx.fillStyle = '#0d0f13'; ctx.fillRect(0, 0, w, height);
        const {peaks} = analysis, per = peaks.length / w;
        ctx.fillStyle = '#3a414d';
        for (let px = 0; px < w; px++) {
            let p = 0; for (let i = Math.floor(px * per); i < Math.floor((px + 1) * per); i++) if (peaks[i] > p) p = peaks[i];
            ctx.fillRect(px, height / 2 - p * height / 2, 1, Math.max(1, p * height));
        }
        const x0 = view.start / analysis.duration * w, x1 = (view.start + mainWidth / view.pps) / analysis.duration * w;
        ctx.strokeStyle = C.accent; ctx.lineWidth = 1.5; ctx.strokeRect(x0, 1, Math.max(3, x1 - x0), height - 2);
    }, [analysis, view, mainWidth]);
    const jump = (e) => {
        const r = wrap.current.getBoundingClientRect();
        const t = (e.clientX - r.left) / r.width * analysis.duration;
        setView(v => ({...v, start: Math.max(0, t - mainWidth / v.pps / 2)}));
    };
    return <div ref={wrap} onMouseDown={jump} onMouseMove={e => e.buttons === 1 && jump(e)} style={{height, cursor: 'pointer'}}>
        <canvas ref={canvas} style={{width: '100%', height, display: 'block', borderRadius: 6}}/>
    </div>;
};

export const AudioEditor = () => {
    const [items, setItems] = useState([]);
    const [selectedId, setSelectedId] = useState(() => { try { return localStorage.getItem('audio.media'); } catch { return null; } });
    const [analysis, setAnalysis] = useState(null);
    const [clips, setClips] = useState({});
    const [view, setView] = useState({start: 0, pps: 40});
    const [selection, setSelection] = useState(null);
    const [snap, setSnap] = useState('bar');
    const [playing, setPlaying] = useState(false);
    const [loop, setLoop] = useState(true);
    const [click, setClick] = useState(false);
    const [playhead, setPlayhead] = useState(null);
    const [fadeIn, setFadeIn] = useState(0);
    const [fadeOut, setFadeOut] = useState(0);
    const [clipName, setClipName] = useState('');
    const [saved, setSaved] = useState(null);
    const audio = useRef(null);
    const ac = useRef(null);
    const lastT = useRef(0);
    const mainWidth = useRef(800);
    const g = gridOf(analysis);

    const refresh = useCallback(() => {
        fetch(`${API}/media`).then(r => r.json()).then(list => {
            setItems(list);
            setSelectedId(id => id && list.some(m => m.id === id) ? id : (list[0] && list[0].id) || null);
        }).catch(() => {});
        fetch(`${API}/clips`).then(r => r.json()).then(setClips).catch(() => {});
    }, []);
    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        if (!items.some(m => m.status === 'analyzing' || m.status === 'pending')) return;
        const t = setTimeout(refresh, 2000);
        return () => clearTimeout(t);
    }, [items, refresh]);

    const selected = items.find(m => m.id === selectedId);
    useEffect(() => {
        try { if (selectedId) localStorage.setItem('audio.media', selectedId); } catch {}
        setAnalysis(null); setSelection(null); setPlayhead(null);
        if (!selectedId || !selected || selected.status !== 'ready') return;
        fetch(`${API}/media/${selectedId}/analysis`).then(r => r.json()).then(a => {
            setAnalysis(a);
            setView({start: 0, pps: Math.max(8, mainWidth.current / Math.min(a.duration, 60))});
        });
    }, [selectedId, selected && selected.status]);   // eslint-disable-line react-hooks/exhaustive-deps

    // Transport: rAF keeps the playhead, loop, fades and metronome in step with the audio element.
    useEffect(() => {
        if (!playing) return;
        let raf;
        const tick = () => {
            const a = audio.current;
            if (!a) return;
            const t = a.currentTime;
            if (selection && t >= selection.end) {
                if (loop) { a.currentTime = selection.start; lastT.current = selection.start; }
                else { a.pause(); setPlaying(false); }
            }
            let vol = 1;
            if (selection && fadeIn > 0 && t < selection.start + fadeIn) vol = Math.max(0, (t - selection.start) / fadeIn);
            if (selection && fadeOut > 0 && t > selection.end - fadeOut) vol = Math.min(vol, Math.max(0, (selection.end - t) / fadeOut));
            a.volume = Math.min(1, Math.max(0, vol));
            if (click && g && t > lastT.current) {
                const k0 = Math.floor(beatIndexAt(g, lastT.current)), k1 = Math.floor(beatIndexAt(g, t));
                if (k1 > k0 && k1 >= 0) beep(((k1 - g.barPhase) % g.beatsPerBar + g.beatsPerBar) % g.beatsPerBar === 0);
            }
            lastT.current = t;
            setPlayhead(t);
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [playing, selection, loop, click, g, fadeIn, fadeOut]);   // eslint-disable-line react-hooks/exhaustive-deps

    const beep = (accent) => {
        if (!ac.current) ac.current = new (window.AudioContext || window.webkitAudioContext)();
        const ctx = ac.current, o = ctx.createOscillator(), gn = ctx.createGain();
        o.frequency.value = accent ? 1760 : 1100;
        gn.gain.setValueAtTime(0.25, ctx.currentTime); gn.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.05);
        o.connect(gn).connect(ctx.destination); o.start(); o.stop(ctx.currentTime + 0.06);
    };

    const play = (from) => {
        const a = audio.current;
        if (!a) return;
        a.currentTime = from ?? (selection ? selection.start : (playhead || 0));
        lastT.current = a.currentTime;
        a.play(); setPlaying(true);
    };
    const stop = () => { audio.current && audio.current.pause(); setPlaying(false); };
    useEffect(() => {
        const onKey = (e) => {
            if (e.target.tagName === 'INPUT') return;
            if (e.code === 'Space') { e.preventDefault(); playing ? stop() : play(); }
            if (e.key === 'Escape') setSelection(null);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    });

    const saveClip = () => {
        if (!selection || !selectedId) return;
        const name = clipName.trim() || `${selected.name} ${barLabel(g, selection.start)}`;
        fetch(`${API}/clips`, {method: 'POST', headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({name, media: selectedId, start: selection.start, end: selection.end, fadeIn, fadeOut})})
            .then(r => r.json()).then(c => { if (c.error) throw new Error(c.error); setSaved(c.name); refresh(); setTimeout(() => setSaved(null), 2500); })
            .catch(e => setSaved(`error: ${e.message}`));
    };
    const loadClip = (c) => {
        setSelection({start: c.start, end: c.end}); setFadeIn(c.fadeIn || 0); setFadeOut(c.fadeOut || 0); setClipName(c.name);
        setView(v => ({...v, start: Math.max(0, c.start - (mainWidth.current / v.pps) * 0.1)}));
    };
    const myClips = useMemo(() => Object.values(clips).filter(c => c.media === selectedId), [clips, selectedId]);
    const bars = selection && g ? (selection.end - selection.start) / (beatLen(g) * g.beatsPerBar) : null;

    return <div style={{display: 'grid', gridTemplateColumns: '260px 1fr', gap: 10, height: '100%', minHeight: 0}}>
        <Library items={items} selectedId={selectedId} onSelect={setSelectedId} onImported={(m) => { refresh(); setSelectedId(m.id); }}/>
        <div style={{...panel, display: 'flex', flexDirection: 'column', gap: 10, padding: 14}} ref={el => { if (el) mainWidth.current = el.clientWidth - 28; }}>
            {!selected ? <div style={{color: C.dim, margin: 'auto'}}>Import a song to start.</div>
                : selected.status !== 'ready' ? <div style={{color: C.dim, margin: 'auto'}}>Analyzing “{selected.name}”… (beats, bars, waveform — about 20 seconds)</div>
                : !analysis ? <div style={{color: C.dim, margin: 'auto'}}>Loading…</div> : <>
                <div style={{display: 'flex', alignItems: 'baseline', gap: 14}}>
                    <div style={{fontSize: 17, fontWeight: 700}}>{selected.name}</div>
                    {g && <div style={{fontSize: 12, color: C.dim}}>{g.bpm.toFixed(2)} BPM · {g.beatsPerBar}/4 · {fmt(analysis.duration)}
                        {' · '}{g.steady ? <span style={{color: C.accent}}>steady grid ±{g.residualMs} ms</span> : <span style={{color: '#ffb347'}}>tempo varies — check the grid</span>}</div>}
                </div>
                <Overview analysis={analysis} view={view} setView={setView} mainWidth={mainWidth.current}/>
                <Waveform analysis={analysis} view={view} setView={setView} selection={selection} setSelection={setSelection}
                    playhead={playhead} snap={snap} fadeIn={fadeIn} fadeOut={fadeOut}
                    onSeek={(t) => { setPlayhead(t); if (playing) play(t); }}/>
                <div style={{display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                    <button onClick={() => playing ? stop() : play()} style={{...btn, background: playing ? C.accent : btn.background, color: playing ? C.bg : C.text, minWidth: 74}}>
                        {playing ? '■ Stop' : '▶ Play'}</button>
                    <label style={{fontSize: 12, color: C.dim}}><input type="checkbox" checked={loop} onChange={e => setLoop(e.target.checked)}/> loop selection</label>
                    <label style={{fontSize: 12, color: C.dim}}><input type="checkbox" checked={click} onChange={e => setClick(e.target.checked)}/> click track</label>
                    <span style={{width: 1, height: 18, background: C.line, margin: '0 4px'}}/>
                    <span style={{fontSize: 12, color: C.dim}}>snap</span>
                    {['bar', 'beat', 'off'].map(m => <button key={m} onClick={() => setSnap(m)} style={{...btn, padding: '3px 9px',
                        background: snap === m ? C.accent : btn.background, color: snap === m ? C.bg : C.text}}>{m}</button>)}
                    <span style={{flex: 1}}/>
                    <span style={{fontSize: 12, color: C.dim}}>space = play/stop · drag = select · click outside / esc = deselect · pinch/⌘ scroll = zoom</span>
                </div>
                <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', borderTop: `1px solid ${C.line}`, paddingTop: 10}}>
                    {selection ? <span style={{fontSize: 13}}>
                        Selection <b>{g ? `bar ${barLabel(g, selection.start)} → ${barLabel(g, selection.end)}` : ''}</b>
                        <span style={{color: C.dim}}> · {fmt(selection.start)}–{fmt(selection.end)} · {bars != null ? `${+bars.toFixed(2)} bars` : ''} {(selection.end - selection.start).toFixed(2)}s</span>
                        <button onClick={() => setSelection(null)} title="Clear selection (Esc)" style={{...btn, padding: '1px 7px', marginLeft: 10}}>✕</button>
                    </span> : <span style={{fontSize: 13, color: C.dim}}>Drag across the waveform to choose the part you want.</span>}
                    <span style={{flex: 1}}/>
                    <label style={{fontSize: 12, color: C.dim}}>fade in <input type="number" min={0} step={0.1} value={fadeIn} onChange={e => setFadeIn(+e.target.value)} style={{...input, width: 56}}/> s</label>
                    <label style={{fontSize: 12, color: C.dim}}>fade out <input type="number" min={0} step={0.1} value={fadeOut} onChange={e => setFadeOut(+e.target.value)} style={{...input, width: 56}}/> s</label>
                    <input value={clipName} onChange={e => setClipName(e.target.value)} placeholder="clip name, e.g. trilemma intro" style={{...input, width: 200}}/>
                    <button onClick={saveClip} disabled={!selection} style={{...btn, background: selection ? C.accent : btn.background, color: selection ? C.bg : C.dim, fontWeight: 700}}>Save clip</button>
                    {saved && <span style={{fontSize: 12, color: saved.startsWith('error') ? '#ff6b6b' : C.accent}}>{saved.startsWith('error') ? saved : `saved “${saved}”`}</span>}
                </div>
                {myClips.length > 0 && <div style={{display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                    {myClips.map(c => <div key={c.id} onClick={() => loadClip(c)} title="Load into the editor" style={{
                        fontSize: 12, padding: '5px 10px', borderRadius: 14, cursor: 'pointer', background: '#1f232b', border: `1px solid ${C.line}`}}>
                        ♪ {c.name} <span style={{color: C.dim}}>{g ? `${barLabel(g, c.start)}–${barLabel(g, c.end)}` : ''}</span>
                    </div>)}
                </div>}
                <audio ref={audio} src={`${API}/media/${selectedId}/audio`} preload="auto" onEnded={() => setPlaying(false)}/>
            </>}
        </div>
    </div>;
};

export default AudioEditor;
